import alert from 'sweetalert2';
import ExcelJS from 'exceljs';
import moment from 'moment';
import SweetAlert from 'src/class/Alerts';
import i18n from 'src/i18n';
import Formatter from 'src/class/Formatter';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

export const ERROR_COLOR = 'FFFDE6E4';
export const SUBTOTAL_COLOR = 'FFD5F9D4';
export const HEADER_COLOR = [107, 166, 82];
export const MAX_DAILY_HOURS = 12;
export const MAX_EXTRA_HOURS = 4;
export const MAX_WEEK_HOURS = 12;

export const launchAlert = (title = i18n.t('labor.sap_report_title')) => {
  return SweetAlert.default(
    title,
    i18n.t('labor.sap_report_text'),
    i18n.t('labor.sap_report_btn_yes'),
    null,
    i18n.t('labor.sap_report_btn_not')
  );
};

export const addRowsWithConditionalFormatting = (
  worksheet,
  infoData,
  markErrors,
  indexToError
) => {
  const modifiedInfoData = infoData.map((row) => [...row]);
  modifiedInfoData.forEach((row) => {
    if (row && row[indexToError]) {
      row.needsStyle = row[indexToError];
    }
    row[indexToError] = null;
  });

  if (markErrors) {
    worksheet.addRows(modifiedInfoData);
    modifiedInfoData.forEach((row, rowIndex) => {
      if (row.needsStyle) {
        const isSubtotalStyle = row.needsStyle === 'subtotal';

        worksheet.getRow(rowIndex + 1).eachCell((cell) => {
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: isSubtotalStyle ? SUBTOTAL_COLOR : ERROR_COLOR },
          };
        });
      }
    });
  } else worksheet.addRows(modifiedInfoData);
};

export const downloadFile = (buffer, fileName, mimeType) => {
  const blob = new Blob([buffer], { type: mimeType });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = fileName;
  link.click();
};

export const generateExcel = async (
  markErrors,
  sheetName,
  headers,
  data,
  indexToError,
  numberColumns = [],
  rowspanInfo = {
    columns: [],
    rowspan: {},
  }
) => {
  const infoData = [headers, ...data];

  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet(sheetName);

  addRowsWithConditionalFormatting(
    worksheet,
    infoData,
    markErrors,
    indexToError
  );

  numberColumns.forEach((element) => {
    const columnB = worksheet.getColumn(element);
    columnB.numFmt = '#,##0.00';
  });

  rowspanInfo.columns.forEach((column) => {
    let currentRow = 2;
    Object.entries(rowspanInfo.rowspan).forEach(([startRow, span]) => {
      const start = parseInt(startRow) + 2;
      const end = start + parseInt(span) - 1;

      if (start === currentRow) {
        const mergeCellRange = `${column}${start}:${column}${end}`;
        worksheet.mergeCells(mergeCellRange);
        worksheet.getCell(`${column}${start}`).alignment = {
          vertical: 'middle',
        };
        currentRow = end + 1;
      }
    });
  });

  const buffer = await workbook.xlsx.writeBuffer();
  downloadFile(
    buffer,
    `${sheetName}.xlsx`,
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  );
  alert.close();
};

export const calculateRowspan = (row, column, body, rowspanInfo) => {
  const startRow = body.indexOf(row);
  const span = rowspanInfo.rowspan[startRow];

  return span && rowspanInfo.columns.includes(column) ? span : 1;
};

export const functionDidParseCell = (data, body, rowspanInfo) => {
  const rowspan = calculateRowspan(
    data.row.raw,
    data.column.dataKey,
    body,
    rowspanInfo
  );
  if (rowspan > 1) {
    data.cell.rowSpan = rowspan;
    data.cell.styles.valign = 'middle';
  }
};

export const functionWillDrawCell = (data, body, rowspanInfo) => {
  if (data.row.index > 0 && data.column.dataKey in rowspanInfo.columns) {
    const prevRowspan = calculateRowspan(
      body[data.row.index - 1],
      data.column.dataKey,
      body,
      rowspanInfo
    );
    if (prevRowspan > 1 && data.row.index < data.row.index - 1 + prevRowspan) {
      return false;
    }
  }
};

const mixinExport = {
  methods: {
    async exportToXlsx() {
      SweetAlert.download(
        i18n.t('mix.mixin_export.wait_while_excel_downloads'),
        alert
      );
      const date = new Date().toISOString().split('T')[0];
      setTimeout(async () => {
        const data = this.tableData.map((detail) => {
          const {
            worker,
            activity,
            subtotal,
            quantity,
            full_date,
            lot_name,
            number_hours,
            business_cc,
            farm_cc,
            activities_cc,
          } = detail;
          const error = false;

          return [
            worker.code,
            activity.homologated_code,
            subtotal,
            quantity,
            '',
            moment(full_date).format('D.MM.YYYY'),
            lot_name || '',
            Number(number_hours),
            `${business_cc ? business_cc : ''}${farm_cc ? farm_cc : ''}${activities_cc ? activities_cc : ''
            }`,
            error,
          ];
        });

        const headers = [
          i18n.t('labor.worker_code_long'),
          i18n.t('common_words.code_sap'),
          i18n.t('common_words.value'),
          i18n.t('common_words.quantity'),
          i18n.t('labor.unit'),
          i18n.t('common_words.date'),
          i18n.t('common_words.allotment'),
          i18n.t('labor.hours_worked_long'),
          i18n.t('common_words.cost_center'),
        ];
        await generateExcel(false, `${i18n.t('labor.sap_activity_sheet')}_${date}`, headers, data, 9);
      }, 1000);
    },

    checkForErrors(detail) {
      const { subtotal, is_duplicate, exceeds_daily_limit, extra_day_hours, is_daily_hour_exceeds } =
        detail;
      const isExtraWeekHouExceed = this.getExtraWeekHourExceedByWorker(detail);

      return (
        !subtotal ||
        is_daily_hour_exceeds == true ||
        (extra_day_hours && Number(extra_day_hours) > MAX_EXTRA_HOURS) ||
        isExtraWeekHouExceed == true
      );
    },

    async exportXLS() {
      launchAlert(
        `${i18n.t('common_words.download')} ${i18n.t('common_words.report')}`
      ).then(async (result) => {
        const markErrors = result.value ? true : false;
        SweetAlert.download(
          i18n.t('mix.mixin_export.wait_while_excel_downloads'),
          alert
        );

        setTimeout(async () => {
          const data = this.tableData.map((detail) => {
            const {
              date_save,
              worker,
              activity_code,
              activity_name,
              quantity,
              number_hours,
              farm_name,
              lot_name,
              subtotal,
              observations,
              time_type,
              activity,
              increment_rates,
            } = detail;
            const isSubtotalRow = !subtotal;
            const hasError = markErrors ? (!isSubtotalRow && this.checkForErrors(detail)) : false;
            const styleMarker = isSubtotalRow ? 'subtotal' : (hasError ? true : false);
            const timeTypeText = this.formatTimeType(time_type);
            const rates = this.formatRates(increment_rates);

            return [
              date_save,
              worker.code,
              `${worker.first_name} ${worker.last_name}`,
              activity_code,
              activity.homologated_code,
              activity_name,
              Number(quantity),
              Number(number_hours),
              activity.activity_values[0].price,
              farm_name,
              lot_name,
              activity.unit_payment_name,
              Number(subtotal),
              timeTypeText,
              rates,
              observations,
              styleMarker,
            ];
          });

          const headers = [
            i18n.t('common_words.date'),
            i18n.t('labor.worker'),
            `${i18n.t('common_words.name')} ${i18n.t('labor.worker')}`,
            i18n.t('common_words.activity'),
            i18n.t('common_words.c_of_homologation'),
            `${i18n.t('common_words.name')} 
                    ${i18n.t('common_words.from').toLowerCase()} 
                    ${i18n.t('common_words.activity')}`,
            i18n.t('common_words.quantity'),
            i18n.t('common_words.duration'),
            i18n.t('labor.unitary_value'),
            `${i18n.t('common_words.name')} ${i18n.t('common_words.farm')}`,
            i18n.t('common_words.allotment'),
            i18n.t('labor.unit'),
            i18n.t('common_words.value_total'),
            i18n.t('labor.hour_type'),
            i18n.t('common_words.increments'),
            i18n.t('common_words.observation'),
          ];
          const date = new Date().toISOString().split('T')[0];
          await generateExcel(
            markErrors,
            `${i18n.t('labor.activity_sheet')}_${date}`,
            headers,
            data,
            16
          );
        }, 1000);
      });
    },

    formatTimeType(timeType) {
      const { price_format, name, price } = timeType;
      const pre = price_format === '$' ? price_format : '';
      const post = price_format === '%' ? price_format : '';
      return `${name} ${pre}${Formatter.formatNumber(
        Number(price)
      )}${post} adicional`;
    },

    formatRates(incrementRates) {
      return incrementRates
        .map(
          (rate) =>
            `${rate.activity_rate.name}: ${Formatter.formatNumber(
              Number(rate.rate_value)
            )}`
        )
        .join(', ');
    },

    async exportToPDF() {
      launchAlert(
        `${i18n.t('common_words.download')} ${i18n.t('common_words.report')}`
      ).then(async (result) => {
        const markErrors = result.value ? true : false;
        SweetAlert.download(
          i18n.t('mix.mixin_export.wait_while_excel_downloads'),
          alert
        );

        setTimeout(() => {
          const data = this.tableData.map((detail) => {
            const {
              date_save,
              worker,
              activity_code,
              activity_name,
              quantity,
              number_hours,
              farm_name,
              lot_name,
              subtotal,
              observations,
              time_type,
              activity,
              increment_rates,
            } = detail;
            const error = markErrors ? this.checkForErrors(detail) : false;
            const timeTypeText = this.formatTimeType(time_type);
            const rates = this.formatRates(increment_rates);

            return [
              date_save,
              worker.code,
              `${worker.first_name} ${worker.last_name}`,
              activity_code,
              activity_name,
              quantity,
              number_hours,
              activity.activity_values[0].price,
              farm_name,
              lot_name,
              activity.unit_payment_name,
              Formatter.formatNumber(Number(subtotal)),
              timeTypeText,
              rates,
              observations,
              error,
            ];
          });

          const columns = [
            { header: i18n.t('common_words.date'), dataKey: '0' },
            { header: i18n.t('labor.worker'), dataKey: '1' },
            {
              header: `${i18n.t('common_words.name')} ${i18n.t(
                'labor.worker'
              )}`,
              dataKey: '2',
            },
            { header: i18n.t('common_words.activity'), dataKey: '3' },
            {
              header: `${i18n.t('common_words.name')} ${i18n.t(
                'common_words.activity'
              )}`,
              dataKey: '4',
            },
            { header: i18n.t('common_words.quantity'), dataKey: '5' },
            { header: i18n.t('common_words.duration'), dataKey: '6' },
            { header: i18n.t('labor.unitary_value'), dataKey: '7' },
            {
              header: `${i18n.t('common_words.name')} ${i18n.t(
                'common_words.farm'
              )}`,
              dataKey: '8',
            },
            { header: i18n.t('common_words.allotment'), dataKey: '9' },
            { header: i18n.t('labor.unit'), dataKey: '10' },
            { header: i18n.t('common_words.value_total'), dataKey: '11' },
            { header: i18n.t('labor.hour_type'), dataKey: '12' },
            { header: i18n.t('common_words.increments'), dataKey: '13' },
            { header: i18n.t('common_words.observation'), dataKey: '14' },
          ];

          const doc = new jsPDF({
            orientation: 'landscape',
            unit: 'mm',
            format: 'a3',
          });

          let body = [];
          if (markErrors) {
            body = data.map((row) => {
              const newRow = [...row];
              if (row[15]) {
                newRow.style = { fillColor: [253, 230, 228] };
              }
              row[15] = null;
              return newRow;
            });
          } else body = [...data];

          const headerStyle = {
            fillColor: HEADER_COLOR,
            textColor: [255, 255, 255],
            fontStyle: 'bold',
            halign: 'left',
          };

          doc.autoTable({
            head: [columns.map((col) => col.header)],
            body: body,
            columns: columns,
            headStyles: headerStyle,
            didParseCell: function (data) {
              // Aplicar estilos personalizados si existen
              if (markErrors && data.row.raw && data.row.raw.style) {
                data.cell.styles.fillColor = data.row.raw.style.fillColor;
              }
            },

            margin: { top: 20, right: 10, bottom: 20, left: 10 },
            styles: { fontSize: 8 },
          });
          const date = new Date().toISOString().split('T')[0];
          doc.save(`${i18n.t('labor.activity_sheet')}_${date}.pdf`);
          alert.close();
        }, 1000);
      });
    },
  },
};

export default mixinExport;
